import Events from '../utils/events';
import { event, init, setConsent } from 'our-insights';

import {
  EV_ADD_TO_CART,
  EV_BEGIN_PAYMENT_FAILED,
  EV_CART_VIEW,
  EV_CHECKOUT_OPTION,
  EV_CHECKOUT_VIEW,
  EV_GENERIC,
  EV_PAGE_VIEW,
  EV_PAYMENT_ABORTED,
  EV_PAYMENT_BROWSER_BACK,
  EV_PRODUCT_VIEW,
  EV_PURCHASE,
  EV_REMOVE_FROM_CART,
  EV_SEARCH,
  EV_UPDATE_POSTAL,
} from './events';
import { getMemberNumber } from '../providers/member';
import { getPostal } from '../providers/postal';
import { EV_COOKIE_CONSENT_UPDATED, ICookie } from '../components-legacy/CookieConsent';
import { getCookieSettings } from '../components-legacy/CookieConsent/useUpdateCookies';

const getProductName = (product: any) => {
  if (!product.description) {
    return product.name;
  }

  if (product.description?.short) {
    return `${product.name} ${product.description?.short}`;
  }

  if (product.description.constructor === String) {
    return `${product.name} ${product.description}`;
  }
};

const pf = (source: string, prefix?: string) =>
  prefix
    ? `${prefix.toLocaleLowerCase()}${source.substring(0, 1).toUpperCase()}${source.substring(1)}`
    : source;

const getTrackableProduct = (product: any, variant?: string, quantity = 1, prefix = '') => ({
  [pf('brand', prefix)]: product.brand?.name || 'MIO',
  [pf('category', prefix)]: product.categorization?.categoryLeafName || '',
  [pf('productType', prefix)]: product.productType,
  [pf('id', prefix)]: product.moderArtikelId || product.m3Id,
  [pf('groupId', prefix)]: product.groupId,
  [pf('name', prefix)]: getProductName(product),
  [pf('price', prefix)]: product.price?.price,
  [pf('originalPrice', prefix)]:
    product.price?.originalPrice === undefined
      ? product.price?.before
      : product.price?.originalPrice,
  [pf('quantity', prefix)]: quantity,
  [pf('variant', prefix)]:
    variant ||
    product.configurations?.['sofaUpholster']?.name ||
    product.specifications?.colorDescription ||
    product.specifications?.colorRange,
});

export default (collector?: string, experiments?: Record<string, string>) => {
  if (!collector) {
    return;
  }

  const consenting = getCookieSettings()?.analyticsCookies;

  if (!consenting) {
    Events.on(EV_COOKIE_CONSENT_UPDATED, (cookies: ICookie[]) => {
      const consent = cookies.find(({ name }) => name === 'analyticsCookies')?.active;
      setConsent(!!consent);
    });
  }

  init({
    collector,
    consenting,
    enrichers: [memberNumberEnricher, postalEnricher],
    events: [{ name: 'pageView', data: { title: document.title } }],
    experiments,
  });

  Events.on(EV_ADD_TO_CART, (product: any, variant?: string, quantity = 1) => {
    try {
      event('addToCart', {
        ...getTrackableProduct(product, variant, quantity, 'p'),
      });
    } catch (e) {
      console.warn('Tracking our insights failed', e);
    }
  });

  Events.on(EV_REMOVE_FROM_CART, (product: any, variant?: string, quantity = 1) => {
    try {
      event('removeFromCart', {
        ...getTrackableProduct(product, variant, quantity, 'p'),
      });
    } catch (e) {
      console.warn('Tracking our insights failed', e);
    }
  });

  Events.on(EV_PRODUCT_VIEW, (product: any, variant?: string) => {
    try {
      event('productView', {
        ...getTrackableProduct(product, variant, 1, 'p'),
      });
    } catch (e) {
      console.warn('Tracking our insights failed', e);
    }
  });

  Events.on(EV_CART_VIEW, ({ products }: { products: any[] }) => {
    try {
      event('cartView', {
        products: products.map((p) => getTrackableProduct(p, undefined, undefined)),
      });
    } catch (e) {
      console.warn('Tracking our insights failed', e);
    }
  });

  Events.on(EV_UPDATE_POSTAL, (postal: string) => {
    try {
      event('updatePostal', {
        newPostal: postal,
      });
    } catch (e) {
      console.warn('Tracking our insights failed', e);
    }
  });

  type CheckoutView = {
    products: any[];
    step: number;
    option?: string;
  };

  Events.on(EV_CHECKOUT_VIEW, ({ products, step, option }: CheckoutView) => {
    try {
      event('checkoutStep', {
        products: products.map((p) => getTrackableProduct(p)),
        chStep: step,
        chOption: option,
      });
    } catch (e) {
      console.warn('Tracking our insights failed', e);
    }
  });

  Events.on(EV_CHECKOUT_OPTION, ({ step, option }: { step: number; option?: string }) => {
    try {
      event('checkoutOption', {
        chOption: option,
        chStep: step,
      });
    } catch (e) {
      console.warn('Tracking our insights failed', e);
    }
  });

  Events.on(
    EV_PURCHASE,
    (products: any[], order: Commerce.IOrderResponse, selectedDeliveryMethods: string) => {
      try {
        event('purchase', {
          puId: order.id,
          puRevenue: order.cart.total,
          puTax: order.cart.vat,
          puShipping: order.cart.deliveryPrice,
          puDeliveryType: selectedDeliveryMethods,
          products: products.map((p) => getTrackableProduct(p)),
        });
      } catch (e) {
        console.warn('Tracking our insights failed', e);
      }
    },
  );

  Events.on(EV_PAGE_VIEW, (title: string) => {
    try {
      event('pageView', {
        title,
      });
    } catch (e) {
      console.warn('Tracking our insights failed', e);
    }
  });

  Events.on(EV_SEARCH, (query: string, result: ICategory, location: string) => {
    try {
      event('search', {
        location,
        sQuery: query,
        sSuggestion: result.didYouMean,
        sProducts: result.totalHits,
        sPages: result.pages?.length || 0,
        sDocuments: result.documents?.length || 0,
        sStores: result.stores?.length || 0,
      });
    } catch (e) {
      console.warn('Tracking our insights failed', e);
    }
  });

  Events.on(EV_PAYMENT_ABORTED, (status: string) => {
    try {
      event('paymentAborted', {
        chReason: status,
      });
    } catch (e) {
      console.warn('Tracking payment aborted failed for our insights', e);
    }
  });

  Events.on(EV_BEGIN_PAYMENT_FAILED, (error: string) => {
    try {
      event('paymentAborted', {
        chReason: error,
      });
    } catch (e) {
      console.warn('Tracking begin payment failed for our insights', e);
    }
  });

  Events.on(EV_PAYMENT_BROWSER_BACK, () => {
    try {
      event('paymentAborted', {
        chReason: 'browserBack',
      });
    } catch (e) {
      console.warn('Tracking payment browser back failed for our insights', e);
    }
  });

  Events.on(EV_GENERIC, ({ event: eventName, ...props }: { event: string }) => {
    try {
      switch (eventName) {
        case 'sofaBuilderOpened': {
          event('sofaBuilderOpened');
          break;
        }
        case 'multi_image_hero_click': {
          event('multi_image_hero_click', { ...props });
          break;
        }
      }
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });
};

const memberNumberEnricher = () => ({ memberNumber: getMemberNumber() });
const postalEnricher = () => ({ postal: getPostal() });
